import { ChartType } from "@components/charts";
import {
    Condition,
    ConditionType,
    IValueInterval
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import {
    DashboardTileType,
    ICustomTileInfo,
    IGetTileDataArgs,
    IInfoTileDataCell,
    IInfoTileInfo,
    ILinkTileInfo,
    ITableTileData,
    ITableTileInfo,
    TDashboardTileInfo
} from "@components/dashboard";
import {
    getDrillDownLink,
    getDrillDownNavParams,
    getTableDrillDownLink,
    getTableIntentLink,
    IGetDrillDownParams
} from "@components/drillDown/DrillDown.utils";
import { formatDateToDateString, IDateInterval, isSameMonth } from "@components/inputs/date/utils";
import { getInfoValue, ifAll, ifAny, IGetValueArgs, not, TInfoValue } from "@components/smart/FieldInfo";
import { IReportRowDef } from "@components/smart/smartTable";
import { IColumn, IRow } from "@components/table";
import AgendaPerformanceOverview from "@components/tiles/agendaPerformanceOverview";
import { IChartTileData } from "@components/tiles/chartTile/ChartTile";
import DemoTenant from "@components/tiles/demoTenant";
import { infoTileCurrencyFormatter } from "@components/tiles/infoTile";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import {
    AccountEntity,
    BankTransactionEntity,
    CashBoxEntity,
    CashReceiptEntity,
    CompoundJournalEntryEntity,
    DocumentEntity,
    ElectronicSubmissionEntity,
    EntitySetName,
    EntityTypeName,
    GrantEntity,
    IBankTransactionEntity,
    ICashBoxEntity,
    ICashReceiptEntity,
    ICompanyEntity,
    ICompoundJournalEntryEntity,
    IDocumentEntity,
    IElectronicSubmissionEntity,
    IFiscalYearEntity,
    IGrantEntity,
    IUserEntity,
    IVatPayerRegistrationLimitEntity,
    JournalEntryEntity,
    VatPayerRegistrationLimitEntity
} from "@odata/GeneratedEntityTypes";
import { AccountingCode, ClearedStatusCode, CurrencyCode, ElectronicSubmissionTypeCode, GeneralPermissionCode, PaymentStatusCode, VatPayerRegistrationActionCode, VatStatementFrequencyCode, VatStatementStatusCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { transformToODataString } from "@odata/OData.utils";
import {
    getUnreadMessagesWithCustomersCount,
    getUnreadMessagesWithCustomersCountCrossCompany
} from "@pages/chats/Chats.utils";
import { getCompanyVatStatementPeriod } from "@pages/companies/Company.shared.utils";
import { VatStatementPeriod } from "@pages/companies/Company.types";
import { isActiveCompany } from "@pages/companies/Company.utils";
import { shouldUseGrant } from "@pages/grant/Grant.utils";
import { AccountAnalysisProps, ClosingAccounts } from "@pages/reports/accountAnalysis/AccountAnalysisDef";
import { CUSTOM_DATE_RANGE_ID } from "@pages/reports/customFilterComponents/ComposedDateRange.utils";
import { buildFilterTree, TBuildReportNodeFilter } from "@pages/reports/ReportFilter.utils";
import {
    isAccountAssignmentCompany,
    isAccountAssignmentCompanyValue,
    isCashBasisAccountingCompany,
    isDemoTenant,
    isOnOrganizationLevel,
    isVatRegisteredCompany
} from "@utils/CompanyUtils";
import { forEachKey, ifPositive, isDefined, isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import { UnitType } from "dayjs";
import i18next from "i18next";
import React, { lazy } from "react";
import { LinkProps } from "react-router-dom";

import AgendaWorkOverview from "../../components/tiles/agendaWorkOverview";
import CustomerSupportTile from "../../components/tiles/customerSupportTile";
import IncomeExpenseOverview from "../../components/tiles/incomeExpenseOverview";
import KeyboardShortcutsTile from "../../components/tiles/keyboardShortcutsTile/KeyboardShortcutsTile";
import PurchaseTile from "../../components/tiles/purchaseTile/PuchaseTile";
import Welcome from "../../components/tiles/welcome";
import {
    ACCOUNT_ANALYSIS_API_URL,
    ACCOUNTING_JOURNAL_API_URL,
    DASHBOARD_CASH_BOXES_URL,
    DASHBOARD_COMPANY_BANK_ACCOUNTS_URL,
    DASHBOARD_DATA_API,
    EMPTY_DASH,
    REST_API_URL
} from "../../constants";
import { AppMode, IAppContext } from "../../contexts/appContext/AppContext.types";
import { LogicOperator, Sort, Status, TextAlign, ValueType } from "../../enums";
import { TRecordAny, TRecordType } from "../../global.types";
import {
    ROUTE_ACCOUNT_ANALYSIS,
    ROUTE_AGENDA_WORK_OVERVIEW,
    ROUTE_BANK_TRANSACTIONS,
    ROUTE_CASH_RECEIPTS,
    ROUTE_CHATS,
    ROUTE_DOCUMENT_JOURNAL,
    ROUTE_INBOX,
    ROUTE_INTERNAL_DOCUMENT,
    ROUTE_JOURNAL_ENTRIES_LAST_MONTH,
    ROUTE_NONPROFIT_GRANT,
    ROUTE_POSTED_DOCUMENTS_LAST_MONTH
} from "../../routes";
import { formatCurrency } from "../../types/Currency";
import DateType, { DATE_MAX, DATE_MIN, DateFormat, getUtcDate, getUtcDayjs, TDateLike } from "../../types/Date";
import customFetch, { getDefaultPostParams } from "../../utils/customFetch";
import memoize from "../../utils/memoize";
import { isNotYetPayedForSubscription } from "../admin/subscriptions/Subscriptions.utils";
import { CustomerGeneralRoleId, isUserOwner, userNameWithAvatarFormatter } from "../admin/users/Users.utils";
import {
    AccrualFilterTypes,
    ACCRUED_DOCUMENTS_FILTERNAME,
    EXPENSES_AND_REVENUES_FILTERNAME,
    ExpensesAndRevenuesFilterTypes
} from "../documents/internalDocument/InternalDocumentDef";
import {
    getDocumentJournalStatusFilter,
    getVatSubmissionPeriodName
} from "../electronicSubmission/VatSubmission.utils";
import { getDateFilterFromFY, getOldestActiveFY, getSortedFYs } from "../fiscalYear/FiscalYear.utils";
import { CommonReportProps, DocumentStatusLocalPath, transformDocumentStatusFilterValue } from "../reports/CommonDefs";
import {
    IReportData,
    IReportSettings,
    NumberAggregationFunction, ReportColumnType,
    ReportFilterNodeColumnType,
    ReportNodeOperator,
    TimeAggregationFunction
} from "../reports/Report.utils";
import { chatsWidgetId } from "./CustomerDashboardDef";
import { CustomerPortal } from "./CustomerPortal.utils";
import { IDashboardManager } from "./DashboardManager";

const CrossCompanyPrefix = "CrossCompany";

const INBOX_DATA_ENDPOINT = "Inbox";
const TICKETS_DATA_ENDPOINT = "Tickets";
const ACCRUED_DATA_ENDPOINT = "Accrued";
const OPEN_ACCRUED_DATA_ENDPOINT = "OpenAccrued";
const ELECTRONIC_SUBMISSION_DATA_ENDPOINT = "ElectronicSubmission";
const MESSAGES = "Chat";

const POSTED_DOCUMENTS_LAST_MONTH = "DocumentCount";
const JOURNAL_ENTRIES_LAST_MONTH = "JournalEntryCount";
const POSTED_DOCUMENTS_LAST_MONTH_PER_USER = "UserDocumentCount";

export const VAT_ACCOUNT_PREFIX = "343";
export type TAccountType = JournalEntryEntity.CreditAccount | JournalEntryEntity.DebitAccount;


export function hasAnyAgenda(args: IGetValueArgs): boolean {
    const { context } = args;
    return !!context.getData()?.companies?.length;
}

export function isInCustomerPortal(): boolean {
    return CustomerPortal.isActive;
}

export function agendaIsVatRegistered(args: IGetValueArgs): boolean {
    return isVatRegisteredCompany(args.context);
}

export function getVisibleDashboards(managers: IDashboardManager[], context: IAppContext): IDashboardManager[] {
    return managers.filter(manager => getInfoValue(manager.definition, "isVisible", { context }));
}

export function companyOverviewPageOr(fallbackRoute: LinkProps["to"]): (args: IGetValueArgs) => LinkProps["to"] {
    return (args) => {
        if (isOnOrganizationLevel(args)) {
            return ROUTE_AGENDA_WORK_OVERVIEW;
        }
        return fallbackRoute;
    };
}

export function drilldownLinkIfOnCompanyLevel(params: IGetDrillDownParams): (args: IGetValueArgs) => LinkProps["to"] {
    return (args) => {
        if (params && !isOnOrganizationLevel(args)) {
            return getDrillDownNavParams(params);
        }
        return null;
    };
}

export function getInfoDataTileCellValueWithSeverity(value: number): IInfoTileDataCell {
    return {
        value: Math.abs(value),
        severity: ifPositive(value, Status.Success, Status.Error)
    };
}

/**
 * Common function to obtain data for dashboard tile in two modes - for exact company or cross all companies,
 * user is allowed to view
 * @param endpoint
 * @param oData
 * @param context
 * @param signal
 */
async function getDashboardData<T>(endpoint: string, oData: OData, context: IAppContext, signal?: AbortSignal): Promise<T> {
    const prefix = isOnOrganizationLevel({ context }) ? CrossCompanyPrefix : "";
    const url = `${DASHBOARD_DATA_API}/${prefix}${endpoint}`;

    try {
        const res = await customFetch(url, { signal });
        return (await res.json()) as T;
    } catch (e) {
        logger.error(`Error fetching dashboard data from endpoint ${endpoint}`, e);
    }
    return {} as T;
}

/**
 * Function to obtain data for some widgets (Inbox, Accrued and OpenAccrued widgets) and overview page
 */
async function getSummaryData(endpoint: string, isCrossCompany: boolean): Promise<IEntity> {
    const prefix = isCrossCompany ? CrossCompanyPrefix : "";
    const url = `${DASHBOARD_DATA_API}/${prefix}${endpoint}`;

    try {
        const res = await customFetch(url);
        return res.json();
    } catch (e) {
        logger.error(`Error fetching summary data from endpoint ${endpoint}`, e);
    }
    return {};
}

async function getSummaryDataSummed<R>(endpoint: string, context: IAppContext, infoPropName: string, additionalProps?: string[]): Promise<R> {
    const data = await getSummaryData(endpoint, isOnOrganizationLevel({ context }));

    const ret: Record<string, number> = {};
    if (!data || isObjectEmpty(data)) {
        return ret as R;
    }
    const companyIds = new Set<number>();
    filterOverviewCompanies(context).forEach(c => companyIds.add(c.Id));

    const _add = (row: TRecordType<Record<string, number> | number>) => {
        const info = row?.[infoPropName] as Record<string, number>;
        if (row?.CompanyId && !companyIds.has(row.CompanyId as number)) {
            // skip summary of data for companies, which user is not allowed to see
            return;
        }

        info && forEachKey(info, (key) => {
            ret[key] = (ret[key] ?? 0) + info[key];
        });
    };

    if (Array.isArray(data)) {
        data.forEach((row) => _add(row));
    } else {
        if (Array.isArray(additionalProps)) {
            additionalProps.forEach(prop => ret[prop] = data[prop]);
        }
        _add(data);
    }

    return ret as R;
}

export const getManualsDef = (): ILinkTileInfo => getLinkTileDef("Home:Links.Manuals", "https://www.evala.cz/navody", "Manual");

/**
 * Inbox tile definition & data --------------------------------------------------------------------
 */
interface IInboxDashboardData {
    Sorted: number;
    Received: number;
}

const getInboxData = (context: IAppContext) => getSummaryDataSummed<IInboxDashboardData>(INBOX_DATA_ENDPOINT, context, "InboxInfo");

export function getChatForAgendaTile(withoutLink?: boolean): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:Chats.Title"),
        link: withoutLink ? null : companyOverviewPageOr(null),
        infoData: async ({ context }) => {
            const isCompanyLevel = context.getAppMode() !== AppMode.OrganizationSettings;
            let count = 0;
            if (isCompanyLevel) {
                count = await getUnreadMessagesWithCustomersCount();
            } else {
                const data = await getUnreadMessagesWithCustomersCountCrossCompany();
                count = data?.reduce?.((sum: number, item: IEntity) => {
                    return sum + item.UnreadChatCount || 0;
                }, 0);
            }
            return [{
                value: count,
                label: i18next.t("Home:Chats.Unread", { count }),
                link: drilldownLinkIfOnCompanyLevel({ route: ROUTE_CHATS, context })
            }];
        },
        size: { w: 1, h: 1 }
    };
}


export function getInboxTileDef(withoutLink?: boolean): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:Inbox.Heading"),
        link: withoutLink ? null : companyOverviewPageOr(null),
        infoData: async ({ context }) => {
            const { Received, Sorted } = await getInboxData(context);
            return [{
                value: Received,
                severity: Received > 0 ? Status.Warning : Status.Success,
                label: i18next.t("Home:Inbox.Unsorted", { count: Received }),
                link: drilldownLinkIfOnCompanyLevel({ route: ROUTE_INBOX, context })
            }, {
                value: Sorted,
                severity: Sorted > 0 ? Status.Warning : Status.Success,
                label: i18next.t("Home:Inbox.Sorted", { count: Sorted }),
                link: drilldownLinkIfOnCompanyLevel({ route: ROUTE_INBOX, context })
            }];
        },
        size: { w: 2, h: 1 }
    };
}

interface IVatSubmissionDashboardData extends Pick<IElectronicSubmissionEntity, "DateSubmission" | "DatePeriodStart" | "DatePeriodEnd"> {
    NextPeriodFrequencyCode: VatStatementFrequencyCode;
}

interface ITicketsDashboardData {
    OpenTickets: number;
    UnreadTickets: number;
}

// link tile info with just badge indicating open chat count
export function getChatsTileDef(): ILinkTileInfo {
    return getLinkTileDef(`Home:CustomerPortalLink.${chatsWidgetId}`, companyOverviewPageOr(ROUTE_CHATS), "Tickets", {
        count: getUnreadMessagesWithCustomersCount
    });
}

/**
 * Users' agenda data -----------------------------------------------------------------------------
 */
export const getUsersAgendaData = async (oData: OData): Promise<ITableTileData> => {
    const rows: IRow[] = [];
    const columns: IColumn[] = [
        { id: "name", label: "", textAlign: TextAlign.Left, width: 180 },
        { id: "count", label: "", textAlign: TextAlign.Right, width: 62 }
    ];

    try {
        const res = await oData.getEntitySetWrapper(EntitySetName.Users).query()
            .filter(`Id ge 0 AND GeneralRole/Id ne ${CustomerGeneralRoleId}`)
            .select("Id", "FirstName", "LastName", "Name")
            .expand("CompanyRoles", (q) => {
                q.count().top(0);
            }).fetchData<IUserEntity[]>();

        for (const r of res.value) {
            rows.push({
                id: r.Id,
                values: {
                    name: userNameWithAvatarFormatter(r.FirstName, { entity: r }, {
                        isBold: true,
                        withLink: false
                    }),
                    count: r._metadata.CompanyRoles.count
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
};

export interface IVatAssessmentTurnoverData {
    Amount: number;
    Status: {
        Code: VatPayerRegistrationActionCode;
        Name: string
    };
}

export async function getVatAssessmentTurnoverData(): Promise<IVatAssessmentTurnoverData> {
    try {
        const res = await customFetch(`${REST_API_URL}/VatPayerRegistrationCheck/Check`);
        if (res.ok) {
            return await res.json() as IVatAssessmentTurnoverData;
        }
    } catch (e) {
        logger.error("Error fetching vat assessment turnover data", e);
    }
    return null;
}

export interface ICrossCompanyGrants {
    CompanyId: string;
    CompanyName: string;
    GrantAmount: number;
    GrantBalanceAmount: number;
    GrantContractNumber: number;
    GrantDeadlineForSettling: Date;
    GrantId: string;
    GrantName: string
    GrantNumberOurs: string;
    GrantShortName: string;
}

export async function getCrossGrantData(): Promise<ICrossCompanyGrants[]> {
    try {
        const res = await customFetch(`${REST_API_URL}/DashboardData/CrossCompanyOpenGrants`);
        if (res.ok) {
            return await res.json() as ICrossCompanyGrants[];
        }
    } catch (e) {
        logger.error("Error fetching vat assessment turnover data", e);
    }
    return null;
}


export async function getVatAssessmentTurnoverLimits(oData: OData): Promise<number[]> {
    const formatedDate = formatDateToDateString(getUtcDate());
    try {
        const limitsRes = await oData.getEntitySetWrapper(EntitySetName.VatPayerRegistrationLimits)
            .query()
            .orderBy(VatPayerRegistrationLimitEntity.Limit, true)
            .filter(`(${VatPayerRegistrationLimitEntity.DateValidTo} eq null OR ${VatPayerRegistrationLimitEntity.DateValidTo} ge ${formatedDate}) AND ${VatPayerRegistrationLimitEntity.DateValidFrom} le ${formatDateToDateString(getUtcDate())}`)
            .fetchData<IVatPayerRegistrationLimitEntity[]>();
        return limitsRes?.value?.map(limit => limit.Limit) ?? [];
    } catch (e) {
        logger.error("Error fetching vat assessment turnover limits", e);
    }
    return null;
}

interface INextSubmission {
    period: VatStatementPeriod;
    submissionDate: Date;
}

// Next electronic submission date
export async function getNextElectronicSubmissionDate(lastSubmission: Date, oData: OData, context: IAppContext): Promise<INextSubmission> {
    let nextTaxPeriod: VatStatementPeriod;

    // FY is needed to get correct nextElectronicSubmissionDate
    await context.getFYPromise();

    if (lastSubmission) {
        nextTaxPeriod = getCompanyVatStatementPeriod(context, lastSubmission, 1);
    } else {
        const firstActiveFY = getOldestActiveFY(context);
        nextTaxPeriod = firstActiveFY && getCompanyVatStatementPeriod(context, firstActiveFY.DateStart);
    }

    let submissionDate: Date;
    if (nextTaxPeriod) {
        // get 25. day of month, which is after nextTaxPeriod.to
        submissionDate = getUtcDayjs(nextTaxPeriod.to).add(1, "day").set("date", 25).toDate();
    }

    return { period: nextTaxPeriod, submissionDate };
}

/**
 * Future expenses and revenues tile definition & data --------------------------------------------------------------------
 */
interface IAccruedDashboardData {
    Expense: number;
    Revenue: number;
}

const getAccruedData = (context: IAppContext) => getSummaryDataSummed<IAccruedDashboardData>(ACCRUED_DATA_ENDPOINT, context, "AccruedInfo");

export const getFutureExpensesAndRevenuesDrilldownParams = (context: IAppContext, type: ExpensesAndRevenuesFilterTypes): IGetDrillDownParams => ({
    route: ROUTE_INTERNAL_DOCUMENT,
    context,
    filters: {
        [EXPENSES_AND_REVENUES_FILTERNAME]: [type]
    }
});

export function getFutureExpensesAndRevenuesDef(isCrossCompany = false): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:FutureExpensesAndRevenues.Heading"),
        link: null,
        infoData: async ({ context }) => {
            const { Expense, Revenue } = await getAccruedData(context);
            return [{
                value: Expense ?? 0,
                label: i18next.t("Home:FutureExpensesAndRevenues.Expenses"),
                link: drilldownLinkIfOnCompanyLevel(getFutureExpensesAndRevenuesDrilldownParams(context, ExpensesAndRevenuesFilterTypes.Expenses))
            }, {
                value: Revenue ?? 0,
                label: i18next.t("Home:FutureExpensesAndRevenues.Revenues"),
                link: drilldownLinkIfOnCompanyLevel(getFutureExpensesAndRevenuesDrilldownParams(context, ExpensesAndRevenuesFilterTypes.Revenues))
            }];
        },
        isVisible: ifAny(isAccountAssignmentCompanyValue, isOnOrganizationLevel),
        size: { w: 2, h: 1 },
        GPs: isCrossCompany ? GeneralPermissionCode.OrganizationManagement : null,
        CPs: isCrossCompany ? null : UserPermissionCodes.InternalDocuments
    };
}

/**
 * estimated accruals tile definition & data --------------------------------------------------------------------
 */
interface IOpenAccruedDashboardDataFiscalYearInfo {
    Id: number;
    DateEnd: Date;
    DateStart: Date;
}

interface IOpenAccruedDashboardData {
    ThisFiscalYear: number;
    LastFiscalYear: number;
    ThisFiscalYearInfo?: IOpenAccruedDashboardDataFiscalYearInfo;
    LastFiscalYearInfo?: IOpenAccruedDashboardDataFiscalYearInfo;
}

const getOpenAccruedData = (context: IAppContext) => getSummaryDataSummed<IOpenAccruedDashboardData>(OPEN_ACCRUED_DATA_ENDPOINT, context, "OpenAccruedInfo", ["ThisFiscalYearInfo", "LastFiscalYearInfo"]);

const getOpenAccruedFilterDrilldownParams = (context: IAppContext, filters: TRecordAny): IGetDrillDownParams => ({
    route: ROUTE_INTERNAL_DOCUMENT,
    context,
    filters: {
        [ACCRUED_DOCUMENTS_FILTERNAME]: [AccrualFilterTypes.Open],
        ...filters
    }
});

interface IOpenAccruedDrilldowns {
    ThisFiscalYearParams: IGetDrillDownParams;
    LastFiscalYearParams: IGetDrillDownParams;
}

export function getOpenAccruedDrilldowns(context: IAppContext, info?: Pick<IOpenAccruedDashboardData, "ThisFiscalYearInfo" | "LastFiscalYearInfo">): IOpenAccruedDrilldowns {
    let FYs: Partial<IFiscalYearEntity>[];
    if (info) {
        FYs = [info?.ThisFiscalYearInfo, info?.LastFiscalYearInfo];
    } else {
        FYs = getSortedFYs(context, Sort.Desc)
            .filter(fy => getUtcDayjs().isSameOrAfter(fy.DateStart, "date")); // filter out future fiscalYears
    }

    const [thisFY, lastFY] = FYs ?? [];

    const LastFiscalYearParams = lastFY && getOpenAccruedFilterDrilldownParams(context, { ...getDateFilterFromFY(lastFY) });
    const ThisFiscalYearParams = thisFY && getOpenAccruedFilterDrilldownParams(context, { ...getDateFilterFromFY(thisFY) });

    return { ThisFiscalYearParams, LastFiscalYearParams };
}

export function getEstimatedAccrualsDef(isCrossCompany = false): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:EstimatedAccruals.Heading"),
        link: null,
        infoData: async ({ context, oData }): Promise<IInfoTileDataCell[]> => {
            const companyId = context.getCompanyId();
            const [data] = await Promise.all([getOpenAccruedData(context)]);
            if (companyId !== context.getCompanyId()) {
                // User switched company in the meantime, stop processing it then as getOpenAccruedDrilldowns
                // may fail on missing FYs
                return [];
            }

            const { ThisFiscalYear, LastFiscalYear, ThisFiscalYearInfo, LastFiscalYearInfo } = data;
            const {
                ThisFiscalYearParams,
                LastFiscalYearParams
            } = getOpenAccruedDrilldowns(context, { ThisFiscalYearInfo, LastFiscalYearInfo });

            return [{
                value: LastFiscalYear ?? 0,
                label: i18next.t("Home:EstimatedAccruals.LastYear"),
                link: drilldownLinkIfOnCompanyLevel(LastFiscalYearParams)
            }, {
                value: ThisFiscalYear ?? 0,
                label: i18next.t("Home:EstimatedAccruals.ThisYear"),
                link: drilldownLinkIfOnCompanyLevel(ThisFiscalYearParams)
            }];
        },
        isVisible: ifAny(isAccountAssignmentCompanyValue, isOnOrganizationLevel),
        size: { w: 2, h: 1 },
        GPs: isCrossCompany ? GeneralPermissionCode.OrganizationManagement : null,
        CPs: isCrossCompany ? null : UserPermissionCodes.InternalDocuments
    };
}

interface IMessageData {
    UnreadChatCount: number;
}

export interface IAgendaWorkOverviewData extends Partial<IInboxDashboardData & ITicketsDashboardData & IAccruedDashboardData & IOpenAccruedDashboardData & IVatSubmissionDashboardData & IMessageData> {
}

export type TAgendaWorkOverviewDataMap = Map<number, IAgendaWorkOverviewData>;

/**
 * Data for agenda work overview
 */
export async function getAgendaWorkOverviewData(): Promise<TAgendaWorkOverviewDataMap> {
    const [inboxData, ticketsData, accruedData, openAccruedData, electronicSubmissionData, messagesData] = await Promise.all([
        getSummaryData(INBOX_DATA_ENDPOINT, true),
        getSummaryData(TICKETS_DATA_ENDPOINT, true),
        getSummaryData(ACCRUED_DATA_ENDPOINT, true),
        getSummaryData(OPEN_ACCRUED_DATA_ENDPOINT, true),
        getSummaryData(ELECTRONIC_SUBMISSION_DATA_ENDPOINT, true),
        getSummaryData(MESSAGES, true)
    ]);

    const ret = new Map<number, IAgendaWorkOverviewData>();

    const _extractRow = (row: TRecordAny, propName: string, additionalInfos?: ("ThisFiscalYearInfo" | "LastFiscalYearInfo")[]) => {
        const companyId = row.CompanyId as number;
        const data = (ret.get(companyId) ?? {}) as IAgendaWorkOverviewData;
        row[propName] && forEachKey(row[propName], (key) => {
            // @ts-ignore
            data[key] = row[propName][key];
            additionalInfos?.forEach((additionalKey) => {
                if (row.hasOwnProperty(additionalKey)) {
                    data[additionalKey] = row[additionalKey];
                }
            });
        });
        ret.set(companyId, data);
    };

    const _extractPlainRow = (row: TRecordAny, propName: string) => {
        const companyId = row.CompanyId as number;
        const data = (ret.get(companyId) ?? {}) as IAgendaWorkOverviewData;
        if (row[propName]) {
            // @ts-ignore
            data[propName] = row[propName];
            ret.set(companyId, data);
        }
    };

    inboxData?.forEach?.((row: TRecordAny) => _extractRow(row, "InboxInfo"));
    ticketsData?.forEach?.((row: TRecordAny) => _extractRow(row, "TicketsInfo"));
    accruedData?.forEach?.((row: TRecordAny) => _extractRow(row, "AccruedInfo"));
    openAccruedData?.forEach?.((row: TRecordAny) => _extractRow(row, "OpenAccruedInfo", ["ThisFiscalYearInfo", "LastFiscalYearInfo"]));
    electronicSubmissionData?.forEach?.((row: TRecordAny) => _extractRow(row, "ElectronicSubmissionInfo"));
    messagesData?.forEach?.((row: TRecordAny) => _extractPlainRow(row, "UnreadChatCount"));
    return ret;
}

interface IPostedDocumentsLastMonthDashboardData {
    Count: number;
    DateRange: {
        DateStart: string;
        DateEnd: string;
    };
}

const getPostedDocumentsLastMonth = (...args: [OData, IAppContext, AbortSignal]) => getDashboardData<IPostedDocumentsLastMonthDashboardData>(POSTED_DOCUMENTS_LAST_MONTH, ...args);

export function getPostedDocumentsLastMonthDef(): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:PostedDocumentsLastMonth.Heading"),
        link: ROUTE_POSTED_DOCUMENTS_LAST_MONTH,
        infoData: async ({ context, oData, signal }) => {
            const { Count, DateRange } = await getPostedDocumentsLastMonth(oData, context, signal);
            const { DateEnd } = DateRange ?? {};
            const period = DateEnd ? DateType.format(getUtcDayjs(DateEnd), DateFormat.monthAndYear) : EMPTY_DASH;
            return [{
                value: Count ?? 0,
                label: i18next.t("Home:PostedDocumentsLastMonth.Label", { period })
            }];
        },
        size: { w: 2, h: 1 },
        GPs: GeneralPermissionCode.OrganizationManagement,
    };
}

interface IJournalEntriesLastMonthLastMonthDashboardData {
    Count: number;
    DateRange: {
        DateStart: string;
        DateEnd: string;
    };
}

const getJournalEntriesLastMonth = (...args: [OData, IAppContext, AbortSignal]) => getDashboardData<IJournalEntriesLastMonthLastMonthDashboardData>(JOURNAL_ENTRIES_LAST_MONTH, ...args);

export function getJournalEntriesLastMonthDef(): IInfoTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:JournalEntriesLastMonth.Heading"),
        link: ROUTE_JOURNAL_ENTRIES_LAST_MONTH,
        infoData: async ({ context, oData, signal }) => {
            const { Count, DateRange } = await getJournalEntriesLastMonth(oData, context, signal);
            const { DateEnd } = DateRange ?? {};
            const period = DateEnd ? DateType.format(getUtcDayjs(DateEnd), DateFormat.monthAndYear) : EMPTY_DASH;
            return [{
                value: Count ?? 0,
                label: i18next.t("Home:JournalEntriesLastMonth.Label", { period })
            }];
        },
        size: { w: 2, h: 1 },
        GPs: GeneralPermissionCode.OrganizationManagement,
    };
}

interface IPostedDocumentsLastMonthPerUserDashboardData {
    User: string;
    Count: number;
}

const getPostedDocumentsLastMonthPerUser = (...args: [OData, IAppContext, AbortSignal]) => getDashboardData<IPostedDocumentsLastMonthPerUserDashboardData[]>(POSTED_DOCUMENTS_LAST_MONTH_PER_USER, ...args);

export function getPostedDocumentsLastMonthPerUserDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:PostedDocumentsLastMonthPerUser.Heading"),
        size: { w: 3, h: 3 },
        GPs: GeneralPermissionCode.OrganizationManagement,
        tooltip: i18next.t("Home:PostedDocumentsLastMonthPerUser.Info"),
        tableData: async ({ context, oData, signal }) => {
            const data = await getPostedDocumentsLastMonthPerUser(oData, context, signal);
            const columns: IColumn[] = [
                {
                    id: "name",
                    label: i18next.t("Home:PostedDocumentsLastMonthPerUser.Name"),
                    textAlign: TextAlign.Left
                },
                {
                    id: "docsCount",
                    label: i18next.t("Home:PostedDocumentsLastMonthPerUser.DocsCount"),
                    textAlign: TextAlign.Right
                }
            ];
            const rows: IRow[] = [];

            if (Array.isArray(data)) {
                for (const row of data) {
                    rows.push({
                        id: rows.length + 1,
                        values: {
                            name: row.User,
                            docsCount: row.Count
                        }
                    });
                }
            }

            return {
                rows,
                columns
            };
        }
    };
}

const _isVatPayer = (args: IGetValueArgs) => isVatRegisteredCompany(args.context);

export const getOldestOpenFyInterval = async (context: IAppContext) => {
    // FY promise is needed to be awaited before reading them.
    await context.getFYPromise();
    // When FY is closed, accounts are zeroed and there is new entry at the first day of new fiscalYear to
    // set initial account balance, so we can show only summary on accounts of opened fiscalYears till now
    const _fromFY = getOldestActiveFY(context);
    return {
        from: _fromFY.DateStart,
        to: getUtcDate()
    };
};

export function getVatTileDef(): TDashboardTileInfo {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:VatOverview.Heading"),
        infoData: async ({ oData, context }) => {
            const interval = await getOldestOpenFyInterval(context);

            let value = 0;
            if (getUtcDayjs(interval.from).isBefore(interval.to, "day")) {
                value = (await getJournalSummaryData(VAT_ACCOUNT_PREFIX, interval))?.[0];
            }

            return [{
                // we display green color for negative values so we invert the sign, the method use abs for value anyway so we can do that
                ...getInfoDataTileCellValueWithSeverity(value * -1),
                label: i18next.t(`Home:VatOverview.${ifPositive(value, "VatLiability", "VatReceivable", "NoVat")}`),
                unit: "Kč"
            }];
        },
        formatter: infoTileCurrencyFormatter,
        isVisible: ifAll(_isVatPayer, isAccountAssignmentCompanyValue),
        size: { w: 2, h: 1 }
    };
}

export function getWelcomeTileDef(): ICustomTileInfo {
    return {
        title: i18next.t("Home:Welcome.Title"),
        type: DashboardTileType.Custom,
        size: { w: 2, h: 2 },
        component: Welcome
    };
}

export function getVideoHelpTileDef(): ICustomTileInfo {
    return {
        title: i18next.t("Home:VideoHelp.Title"),
        type: DashboardTileType.Custom,
        size: { w: 2, h: 2 },
        component: lazy(() => import("@components/tiles/videoHelpTile/VideoHelpTile"))
    };
}

export function getLinkTileDef(title: string, link: TInfoValue<LinkProps["to"]>, iconName: string, props?: Partial<ILinkTileInfo>): ILinkTileInfo {
    return {
        type: DashboardTileType.Link,
        size: { w: 1, h: 1 },
        title: i18next.t(title), link, iconName,
        ...(props ?? {})
    };
}

export function getPurchaseTileDef(): ICustomTileInfo {
    return {
        title: i18next.t("Home:Purchase.Title"),
        type: DashboardTileType.Custom,
        size: { w: 2, h: 2 },
        component: PurchaseTile,
        isVisible: (args: IGetValueArgs) => {
            const data = args.context.getData();

            if (!isUserOwner(data?.userSettings)) {
                return false;
            }

            const subscription = data?.subscription;

            return isNotYetPayedForSubscription(subscription);
        },
        cannotBeRemovedInSettings: true
    };
}

export function getKeyboardShortcutsTileDef(): ICustomTileInfo {
    return {
        title: i18next.t("Home:KeyboardShortcuts.Heading"),
        type: DashboardTileType.Custom,
        size: { w: 3, h: 3 },
        component: KeyboardShortcutsTile
    };
}

export function getBankAccountsTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:BankAccounts.Heading"),
        size: { w: 3, h: 2 },
        tableData: args => getBalancesData(args),
        CPs: UserPermissionCodes.Bank
    };
}

export function getPaymentDocumentTileDef(entitySet: EntitySetName.BankTransactions | EntitySetName.CashReceipts): IInfoTileInfo {
    const MainEntity = entitySet === EntitySetName.BankTransactions ? BankTransactionEntity : CashReceiptEntity;
    return {
        type: DashboardTileType.Info,
        title: i18next.t(`Home:${entitySet}.Heading`),
        link: entitySet === EntitySetName.BankTransactions ? ROUTE_BANK_TRANSACTIONS : ROUTE_CASH_RECEIPTS,
        infoData: async ({ oData }) => {
            let transactions: (IBankTransactionEntity | ICashReceiptEntity)[];
            try {
                const res = await oData.getEntitySetWrapper(entitySet)
                    .query()
                    .filter(`${MainEntity.PaymentStatusCode} in ('${PaymentStatusCode.NeedsApproval}', '${PaymentStatusCode.WaitsForProcessing}', '${PaymentStatusCode.PartiallyProcessed}')`)
                    .select(`${MainEntity.ExchangeRatePerUnit}, ${MainEntity.TransactionAmountDue}`)
                    .fetchData<(IBankTransactionEntity | ICashReceiptEntity)[]>();

                transactions = res.value ?? [];
            } catch (e) {
                logger.error(`Error fetching ${entitySet} for payment document tile`, e);
                transactions = [];
            }

            const totalAmountDue = transactions.reduce((sum: number, t) => {
                sum += Math.abs(t.TransactionAmountDue) * t.ExchangeRatePerUnit;
                return sum;
            }, 0);

            return [{
                value: transactions.length,
                severity: transactions.length ? Status.Warning : Status.Success,
                label: i18next.t(`Home:${entitySet}.Documents`, { count: transactions.length })
            }, {
                value: totalAmountDue,
                formatter: infoTileCurrencyFormatter,
                label: i18next.t(`Home:${entitySet}.Amount`),
                unit: "Kč",
                size: 2
            }];
        },
        size: { w: 3, h: 1 },
        CPs: entitySet === EntitySetName.BankTransactions ? UserPermissionCodes.Bank : UserPermissionCodes.CashBox
    };
}

export function getGrantTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:Grant.Heading"),
        isVisible: (args: IGetValueArgs, otherArgs?: TRecordAny) => {
            return shouldUseGrant(args.context);
        },
        size: { w: 3, h: 2 },
        tableData: args => getGrantData(args)
    };
}

export function getCashBoxTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:CashBox.Heading"),
        size: { w: 3, h: 2 },
        tableData: args => getBalancesData(args, false),
        CPs: UserPermissionCodes.CashBox
    };
}


export function getCustomerSupportTileDef(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t("Home:CustomerSupport.Heading"),
        size: { w: 2, h: 1 },
        component: CustomerSupportTile
    };
}

const getGrantData = async (args: IGetTileDataArgs) => {
    const rows: IRow[] = [];
    const columns: IColumn[] = [
        { id: "name", label: i18next.t(`Home:Grant.Name`), textAlign: TextAlign.Left },
        { id: "amount", label: i18next.t(`Home:Grant.Amount`), textAlign: TextAlign.Right },
        { id: "balanceAmount", label: i18next.t(`Home:Grant.BalanceAmount`), textAlign: TextAlign.Right }
    ];

    try {
        const res = await args.oData.getEntitySetWrapper(EntitySetName.Grants)
            .query()
            .select(GrantEntity.Id, GrantEntity.ShortName, GrantEntity.Balance)
            .filter(`${GrantEntity.IsClosed}  ne true`)
            .expand(GrantEntity.Balance)
            .fetchData<IGrantEntity[]>();

        for (const grant of res.value) {
            rows.push({
                id: grant.Id,
                values: {
                    "name": getTableIntentLink(grant.ShortName, {
                        route: `${ROUTE_NONPROFIT_GRANT}/${grant.Id}`,
                        context: args.context
                    }),
                    "amount": formatCurrency(grant.Balance?.Amount, CurrencyCode.CzechKoruna),
                    "balanceAmount": formatCurrency(grant.Balance?.BalanceAmount, CurrencyCode.CzechKoruna)
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
};

const getBalancesData = async (args: IGetTileDataArgs, isBank = true): Promise<ITableTileData> => {
    const columns: IColumn[] = [
        { id: "name", label: i18next.t(`Home:BankAccounts.Name`), textAlign: TextAlign.Left },
        { id: "balance", label: i18next.t(`Home:BankAccounts.TransactionBalance`), textAlign: TextAlign.Right },
        { id: "accountingBalance", label: i18next.t(`Home:BankAccounts.AccountingBalance`), textAlign: TextAlign.Right }
    ];
    const rows: IRow[] = [];

    try {
        const url = isBank? DASHBOARD_COMPANY_BANK_ACCOUNTS_URL: DASHBOARD_CASH_BOXES_URL;
        const res = await customFetch(url);

        const data = await res.json() || [];
        for (const acc of data) {
            const accountingBalance = formatCurrency(acc.AccountingBalance, acc.TransactionCurrencyCode);
            rows.push({
                id: acc.Id,
                values: {
                    name: {
                        tooltip: acc.Name,
                        value: (<b>{acc.Name}</b>)
                    },
                    balance: formatCurrency(acc.TransactionBalance, acc.TransactionCurrencyCode),
                    accountingBalance: {
                        tooltip: accountingBalance,
                        value: getDrillDownLink(accountingBalance, {
                            route: ROUTE_ACCOUNT_ANALYSIS,
                            filters: {
                                [AccountAnalysisProps.accounts]: [acc.BalanceSheetAccountNumber],
                            },
                            context: args.context
                        })
                    }
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
};

export function getNotClearedDocumentsDef(): IInfoTileInfo {
    const filteredStatuses = [ClearedStatusCode.NotCleared, ClearedStatusCode.PartiallyCleared];

    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:NotClearedDocuments.Heading"),
        link: ({ context }: IGetValueArgs) => {
            const journalFilter = getDocumentJournalStatusFilter(EntityTypeName.ClearedStatus, filteredStatuses);
            const previousYearDateRange: IValueInterval = {
                from: formatDateToDateString(DATE_MIN),
                to: formatDateToDateString(DATE_MAX)
            };

            return getDrillDownNavParams({
                route: ROUTE_DOCUMENT_JOURNAL,
                context,
                filters: {
                    [journalFilter.id]: journalFilter.value,
                    [CommonReportProps.dateRange]: CUSTOM_DATE_RANGE_ID,
                    [CommonReportProps.dateRangeCustomValue]: previousYearDateRange
                }
            });
        },
        infoData: async ({ oData }) => {
            let count: number;

            try {
                const res = await oData.getEntitySetWrapper(EntitySetName.Documents)
                    .query()
                    .filter(`${DocumentEntity.ClearedStatusCode} in (${transformToODataString(filteredStatuses, ValueType.String)})`)
                    .select(`${DocumentEntity.Id}`)
                    .count()
                    .top(0)
                    .fetchData<IDocumentEntity[]>();

                count = res._metadata.count ?? 0;
            } catch (e) {
                logger.error("Error fetching not cleared documents count for tile", e);
            }

            return [{
                value: count,
                label: i18next.t("Home:NotClearedDocuments.Label", { count }),
                severity: Status.Warning
            }];
        },
        size: { w: 2, h: 1 },
        CPs: [UserPermissionCodes.Reports]
    };
}

export function getIncomeExpenseOverviewTileDef(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t("Home:IncomeExpenseOverview.Heading"),
        isVisible: (args: IGetValueArgs) => {
            return isCashBasisAccountingCompany(args.context);
        },
        component: IncomeExpenseOverview,
        size: { w: 3, h: 2 }
    };
}

export function getAgendaWorkOverviewTileDef(isCrossCompany: boolean): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t(`Home:AgendaWorkOverview.${isCrossCompany ? "Title" : "SingleCompanyTitle"}`),
        component: AgendaWorkOverview,
        size: { w: 4, h: 2 },
        isVisible: (args: IGetValueArgs) => {
            return !isCashBasisAccountingCompany(args.context);
        },
        CPs: isCrossCompany ? null : [UserPermissionCodes.InternalDocuments, UserPermissionCodes.InBox]
    };
}

export function getAgendaWorkOverviewTileDefCashBasisAccounting(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t("Home:AgendaWorkOverview.SingleCompanyTitle"),
        component: AgendaWorkOverview,
        size: { w: 2, h: 2 },
        isVisible: (args: IGetValueArgs) => {
            return isCashBasisAccountingCompany(args.context);
        },
        CPs: [UserPermissionCodes.InternalDocuments, UserPermissionCodes.InBox]
    };
}

export function getAgendaPerformanceOverviewTileDef(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t("Home:AgendaPerformanceOverview.Title"),
        component: AgendaPerformanceOverview,
        size: { w: 2, h: 2 },
        GPs: GeneralPermissionCode.OrganizationManagement,
    };
}

export function getDemoTenantTileDef(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t("Home:DemoTenant.Title"),
        isVisible: (args: IGetValueArgs) => {
            return !isDemoTenant(args.context);
        },
        component: DemoTenant,
        size: { w: 1, h: 1 }
    };
}

export async function getVatOverviewChartTileData(args: IGetTileDataArgs): Promise<IChartTileData<ChartType.Bar>> {
    let entries: ICompoundJournalEntryEntity[] = [];
    let electronicSubmissions: IElectronicSubmissionEntity[];
    try {
        const electronicSubmissionsRes = await args.oData.getEntitySetWrapper(EntitySetName.ElectronicSubmissions).query()
            .filter(`${ElectronicSubmissionEntity.ElectronicSubmissionTypeCode} eq '${ElectronicSubmissionTypeCode.VatStatement}'`)
            .orderBy(ElectronicSubmissionEntity.DatePeriodEnd, false)
            .top(12)
            .fetchData<IElectronicSubmissionEntity[]>();
        electronicSubmissions = electronicSubmissionsRes.value?.reverse() ?? [];
        if (electronicSubmissions[0]?.DatePeriodStart) {
            const dateESVSpath = createPath(CompoundJournalEntryEntity.Document, DocumentEntity.DateDocumentVatStatement);
            const entriesRes = await args.oData.getEntitySetWrapper(EntitySetName.CompoundJournalEntries).query()
                .filter(`startswith(${CompoundJournalEntryEntity.Account}/${AccountEntity.Number}, '${VAT_ACCOUNT_PREFIX}')
                AND ${dateESVSpath} ge ${transformToODataString(electronicSubmissions[0]?.DatePeriodStart, ValueType.Date)} AND ${createPath(CompoundJournalEntryEntity.Document, DocumentEntity.VatStatementStatusCode)} in ('${VatStatementStatusCode.Filed}','${VatStatementStatusCode.FiledAndModified}')`)
                .groupBy(dateESVSpath)
                .aggregate(`${CompoundJournalEntryEntity.CreditAmount} with sum as CreditAmount, ${CompoundJournalEntryEntity.DebitAmount} with sum as DebitAmount`)
                .fetchData<ICompoundJournalEntryEntity[]>();

            entries = entriesRes.value;
        }
    } catch (e) {
        logger.error("Error fetching vat overview chart data", e);
    }

    const labels = electronicSubmissions.map(es => {
        const freq = isSameMonth(es.DatePeriodStart, es.DatePeriodEnd) ? VatStatementFrequencyCode.Monthly : VatStatementFrequencyCode.Quarterly;
        return getVatSubmissionPeriodName(es.DatePeriodStart, freq, true);
    }) ?? [];

    const credit: number[] = electronicSubmissions.map((es) => {
        let sum = 0;
        for (const entry of entries) {
            if (getUtcDayjs(entry.Document.DateDocumentVatStatement).isBetween(es.DatePeriodStart, es.DatePeriodEnd, "day", "[]")) {
                sum += entry.CreditAmount;
            }
        }
        return sum;
    });

    const debit: number[] = electronicSubmissions.map((es) => {
        let sum = 0;
        for (const entry of entries) {
            if (getUtcDayjs(entry.Document.DateDocumentVatStatement).isBetween(es.DatePeriodStart, es.DatePeriodEnd, "day", "[]")) {
                sum += entry.DebitAmount;
            }
        }
        return sum;
    });

    return {
        type: ChartType.Bar,
        chartProps: {
            labels,
            values: [debit, credit],
            colors: ["C_CHART_green", "C_CHART_red"],
            dataSetLabels: [i18next.t("Home:VatOverviewChart.Income"), i18next.t("Home:VatOverviewChart.Expense")]
        }
    };
}


export function timeAggregationToOpUnitType(aggregation: TimeAggregationFunction): UnitType {
    switch (aggregation) {
        case TimeAggregationFunction.Day:
            return "day";
        case TimeAggregationFunction.Month:
            return "month";
        case TimeAggregationFunction.Year:
            return "year";
        case TimeAggregationFunction.Week:
        case TimeAggregationFunction.Quarter:
            console.error("Not supported aggregation", aggregation);
            return null;
    }
}

export function getChartLabels(interval: IDateInterval, step: TimeAggregationFunction = TimeAggregationFunction.Month, format = "M/YY"): string[] {
    const labels: string[] = [];
    let i = getUtcDayjs(interval.from);
    const unit = timeAggregationToOpUnitType(step);
    while (i.isSameOrBefore(interval.to, unit)) {
        labels.push(DateType.format(i, format));
        i = i.add(1, unit);
    }
    return labels;
}


function getAccountAnalysisParams(account: string, interval: IDateInterval, aggregationFn?: TimeAggregationFunction): IReportSettings {
    const filterColumn = {
        ColumnAlias: "Account_Number"
    };
    const Groups = aggregationFn ? [{
        ColumnAlias: "JournalEntry_DateAccountingTransaction",
        AggregationFunction: aggregationFn
    }] : [];

    return {
        DateRange: {
            DateStart: formatDateToDateString(interval.from),
            DateEnd: formatDateToDateString(interval.to)
        },
        Filter: {
            Left: filterColumn,
            Right: { Value: account },
            Type: ReportFilterNodeColumnType.String,
            Operator: ReportNodeOperator.StartsWith
        },
        ReportHierarchy: {
            Aggregate: true,
            Groups,
            Columns: [filterColumn],
            Aggregations: [{
                ColumnAlias: "JournalEntry_Amount",
                AggregationFunction: NumberAggregationFunction.Sum
            }]
        },
        ClosingAccounts: ClosingAccounts.DoNotShow,
        SumAmounts: true
    };
}

/**
 * Returns summary for journalEntries per account
 * @param account
 * @param interval
 * @param aggregationFn
 */
export const getAccountAnalysisData = memoize(async (account: string, interval: IDateInterval, aggregationFn?: TimeAggregationFunction): Promise<number[]> => {
    const aggregatedPoints = getChartLabels(interval, aggregationFn, "YYYY-MM-DD");
    const opUnit = isDefined(aggregationFn) ? timeAggregationToOpUnitType(aggregationFn) : null;

    const _extractTotalRowAmount = (rows: IReportRowDef[]): number => {
        const totalRow = rows?.find(row => row.Type === "Total");
        return (totalRow?.Value["JournalEntry_Amount_SUM"] ?? 0) as number;
    };

    const _extractAggregatedData = (rows: IReportRowDef[]): number[] => {
        return aggregatedPoints.map(formattedDate => {
            const groupedResult = rows?.find(row => row.Type === "Group" && getUtcDayjs(row.Value[`JournalEntry_DateAccountingTransaction_${aggregationFn}`] as TDateLike).isSame(getUtcDayjs(formattedDate), opUnit));
            return groupedResult ? _extractTotalRowAmount(groupedResult.Rows) : 0;
        });
    };

    try {
        const params = getAccountAnalysisParams(account, interval, aggregationFn);
        return await customFetch(ACCOUNT_ANALYSIS_API_URL, {
            ...getDefaultPostParams(),
            body: JSON.stringify(params)
        })
            .then(response => response.json())
            .then((data: IReportData) => aggregationFn ? _extractAggregatedData(data.Rows) : [_extractTotalRowAmount(data.Rows)]);
    } catch (e) {
        logger.error(e);
        return [];
    }
}, (account, interval, aggregationFn) => [account, interval.from, interval.to, aggregationFn]);

function createAccountFilters(accounts: string[], columnAliasName: string, condition = Condition.BeginsWith): TBuildReportNodeFilter[] {
    const filterColumn = {
        ColumnAlias: columnAliasName
    };

    return accounts.map(account => ({
        id: filterColumn.ColumnAlias,
        bindingContext: new BindingContext({
            isLocal: true,
            path: filterColumn.ColumnAlias
        }),
        value: {
            type: ConditionType.Included,
            value: account,
            condition
        },
        columnDef: {
            ColumnAlias: filterColumn.ColumnAlias,
            Type: ReportColumnType.String
        }
    }));
}


function getAccountingJournalParams(type: TAccountType, accounts: string[], interval: IDateInterval, aggregationFn?: TimeAggregationFunction, condition = Condition.BeginsWith): IReportSettings {
    const filterColumn = {
        ColumnAlias: type === JournalEntryEntity.CreditAccount ? "CreditAccount_Number" : "DebitAccount_Number"
    };
    const Groups = aggregationFn ? [{
        ColumnAlias: "JournalEntry_DateAccountingTransaction",
        AggregationFunction: aggregationFn
    }] : [];

    const filters = createAccountFilters(accounts, filterColumn.ColumnAlias, condition);
    const Filter = buildFilterTree({ filters, operator: LogicOperator.Or });

    return {
        Filter,
        DateRange: {
            DateStart: formatDateToDateString(interval.from),
            DateEnd: formatDateToDateString(interval.to)
        },
        ReportHierarchy: {
            Aggregate: true,
            Groups,
            Columns: [filterColumn],
            Aggregations: [{
                ColumnAlias: "JournalEntry_Amount",
                AggregationFunction: NumberAggregationFunction.Sum
            }]
        }
    };
}

export interface IJournalSoloData {
    account: string,
    name: string,
    amount: number,
    creditAmount: number,
    debitAmount: number
}

export const getAnalysisReportData =
    memoize(async (accounts: string[], interval: IDateInterval, condition = Condition.BeginsWith): Promise<IJournalSoloData[]> => {
        const filters = createAccountFilters(accounts, "Account_Number", condition);
        const Filter = buildFilterTree({ filters, operator: LogicOperator.Or });

        const params = {
            Filter,
            DateRange: {
                DateStart: formatDateToDateString(interval.from),
                DateEnd: formatDateToDateString(interval.to)
            },
            ClosingAccounts: "Show",
            ReportHierarchy: {
                Columns: [{
                    ColumnAlias: "Account_Number"
                }],
                Groups: [{ ColumnAlias: "Account" }],
                Aggregate: true,
                Aggregations: [
                    { ColumnAlias: "JournalEntry_Debit", AggregationFunction: "SUM" },
                    { ColumnAlias: "JournalEntry_Credit", AggregationFunction: "SUM" }
                ]
            }
        };

        try {
            const request = await customFetch(ACCOUNT_ANALYSIS_API_URL, {
                ...getDefaultPostParams(),
                body: JSON.stringify(params)
            });

            const response = await request.json();

            return response.Rows
                ?.slice(0, -1)
                .map((row: IEntity) => {
                    const valueRow = row.Rows[0].Value;

                    return {
                        name: valueRow.Account,
                        account: valueRow.Account_Number,
                        amount: valueRow.JournalEntry_Credit_SUM - valueRow.JournalEntry_Debit_SUM,
                        creditAmount: valueRow.JournalEntry_Credit_SUM,
                        debitAmount: valueRow.JournalEntry_Debit_SUM
                    };
                }) || [];
        } catch (e) {
            logger.error(e);
            return [];
        }
    }, (accounts, interval) => [accounts, interval.from, interval.to]);


export const getJournalSingleData =
    memoize(async (accounts: string[], interval: IDateInterval, condition = Condition.BeginsWith): Promise<IJournalSoloData[]> => {
        const types = [JournalEntryEntity.CreditAccount, JournalEntryEntity.DebitAccount] as TAccountType[];

        try {
            const promises = types.map(accountType => {
                const params = getAccountingJournalParams(accountType, accounts, interval, undefined, condition);
                params.ReportHierarchy.Columns.push({ ColumnAlias: accountType });

                return customFetch(ACCOUNTING_JOURNAL_API_URL, {
                    ...getDefaultPostParams(),
                    body: JSON.stringify(params)
                })
                    .then(response => response.json())
                    .then((data: IReportData) => {
                        const columnAlias = accountType === JournalEntryEntity.CreditAccount ? "CreditAccount_Number" : "DebitAccount_Number";
                        return data.Rows
                            .filter(row => row.Value[columnAlias])
                            .map(row => ({
                                name: row.Value[accountType],
                                account: row.Value[columnAlias],
                                amount: (row.Value.JournalEntry_Amount_SUM ?? 0) as number
                            }));
                    });
            });

            const [credits, debits] = await Promise.all(promises);
            const ret: IJournalSoloData[] = [];

            const debitMap = new Map(
                debits.map(debit => [debit.account, debit.amount])
            );

            credits.forEach(credit => {
                const debitAmount = debitMap.get(credit.account) ?? 0;

                ret.push({
                    name: credit.name as string,
                    account: credit.account as string,
                    amount: credit.amount - debitAmount,
                    creditAmount: credit.amount,
                    debitAmount: debitAmount
                });
            });

            return ret;
        } catch (e) {
            logger.error(e);
            return [];
        }

    }, (accounts, interval) => [accounts, interval.from, interval.to]);

/**
 * Returns summary for journalEntries per account
 * @param account
 * @param interval
 * @param aggregationFn
 */
export const getJournalSummaryData = memoize(async (account: string, interval: IDateInterval, aggregationFn?: TimeAggregationFunction): Promise<number[]> => {
    const types = [JournalEntryEntity.CreditAccount, JournalEntryEntity.DebitAccount] as TAccountType[];
    const aggregatedPoints = getChartLabels(interval, aggregationFn, "YYYY-MM-DD");
    const opUnit = isDefined(aggregationFn) ? timeAggregationToOpUnitType(aggregationFn) : null;

    const _extractTotalRowAmount = (rows: IReportRowDef[]): number => {
        const totalRow = rows?.find(row => row.Type === "Total");
        return (totalRow?.Value["JournalEntry_Amount_SUM"] ?? 0) as number;
    };

    const _extractAggregatedData = (rows: IReportRowDef[]): number[] => {
        return aggregatedPoints.map(formattedDate => {
            const groupedResult = rows?.find(row => row.Type === "Group" && getUtcDayjs(row.Value[`JournalEntry_DateAccountingTransaction_${aggregationFn}`] as TDateLike).isSame(getUtcDayjs(formattedDate), opUnit));
            return groupedResult ? _extractTotalRowAmount(groupedResult.Rows) : 0;
        });
    };

    const ret: number[] = [];

    try {
        const promises = types.map(accountType => {
            const params = getAccountingJournalParams(accountType, [account], interval, aggregationFn);

            return customFetch(ACCOUNTING_JOURNAL_API_URL, {
                ...getDefaultPostParams(),
                body: JSON.stringify(params)
            })
                .then(response => response.json())
                .then((data: IReportData) => aggregationFn ? _extractAggregatedData(data.Rows) : [_extractTotalRowAmount(data.Rows)]);
        });

        const [credit, debit] = await Promise.all(promises);
        credit.forEach((num, idx) => {
            ret.push(num - debit[idx]);
        });

    } catch (e) {
        logger.error(e);
        return [];
    }

    return ret;
}, (account, interval, aggregationFn) => [account, interval.from, interval.to, aggregationFn]);

export interface IFyCloseOverviewData {
    AccountingCode: AccountingCode;
    Company: string;
    CompanyId: number;
    DateEnd: Date;
    DateStart: Date;
    FiscalYearNumber: string;
    SectionCount: number;
    SectionDone: number;
}

export type TFyCloseOverviewData = Map<number, IFyCloseOverviewData>;

export const getFYcloseOverviewData = async (): Promise<TFyCloseOverviewData> => {
    const ret = new Map<number, IFyCloseOverviewData>();
    try {
        const res = await customFetch(`${REST_API_URL}/DashboardData/CrossCompanyFiscalYearClosed`);
        const fycData = await res.json() as IFyCloseOverviewData[];
        if (fycData && Array.isArray(fycData)) {
            fycData.forEach((row) => {
                ret.set(row.CompanyId, row);
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return ret;
};

export const getAssetAnalysisData = memoize(async (FYId: number): Promise<number> => {
    const assetAnalysisProps: TRecordAny = {
        "FiscalYearId": FYId,
        "ReportHierarchy": {
            "Aggregate": true,
            "Groups": [],
            "Columns": [],
            "Aggregations": [{
                ColumnAlias: "Asset_CalculatedPrice",
                AggregationFunction: NumberAggregationFunction.Sum
            }]
        }
    };

    try {
        const res = await customFetch(`${REST_API_URL}/AssetAnalysis`, {
            ...getDefaultPostParams(),
            body: JSON.stringify(assetAnalysisProps)
        });

        const data = await res.json();
        return data?.Rows?.[0]?.Value?.["Asset_CalculatedPrice_SUM"] ?? 0;
    } catch (e) {
        logger.error(e);
    }

    return 0;
});

export function filterOverviewCompanies(context: IAppContext, data?: Map<number, unknown>): ICompanyEntity[] {
    const companies = context.getData().companies as ICompanyEntity[];
    return companies.filter(c => (!data || data.has(c.Id)) && isActiveCompany(c));
}


export const isCashBasisAccountingCompanyValue = (args: IGetValueArgs): boolean => isCashBasisAccountingCompany(args.context ?? args.storage.context);

export const getProfitLossTileDef = (): IInfoTileInfo => {
    return {
        type: DashboardTileType.Info,
        title: i18next.t("Home:ProfitLoss.Header"),
        size: { w: 4, h: 1 },
        isVisible: not(isCashBasisAccountingCompanyValue),
        formatter: infoTileCurrencyFormatter,
        infoData: getProfitLossData,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    };
};

export const getCreditAccountBalancesTileDef = (): ITableTileInfo => {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:CreditAccountBalances.Header"),
        size: { w: 3, h: 2 },
        isVisible: not(isCashBasisAccountingCompanyValue),
        tableData: getCreditAccountBalancedTableData,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    };
};

export const getLiabilitiesToShareholdersTileDef = (): ITableTileInfo => {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:LiabilitiesToShareholders.Header"),
        isVisible: not(isCashBasisAccountingCompanyValue),
        size: { w: 3, h: 2 },
        noDataText: i18next.t("Home:LiabilitiesToShareholders.NoDataText"),
        tableData: getLiabilitiesToShareholdersTableData,
        CPs: [CustomerPermissionCodes.AccountingReports, UserPermissionCodes.CustomerPortal]
    };
};

const createAccountRows = (accounts: IJournalSoloData[], context: IAppContext, interval: IDateInterval) => {
    const dateRange = {
        from: formatDateToDateString(interval.from),
        to: formatDateToDateString(interval.to)
    };

    return accounts?.map(acc => ({
            id: acc.account,
            values: {
                Account: {
                    tooltip: acc.name,
                    value: (
                        <strong>{acc.name}</strong>
                    )
                },
                Amount: getTableDrillDownLink(formatCurrency(acc.amount, CurrencyCode.CzechKoruna), {
                    route: ROUTE_ACCOUNT_ANALYSIS,
                    filters: {
                        [AccountAnalysisProps.closingAccounts]: "Show",
                        [AccountAnalysisProps.sumAmounts]: true,
                        [BindingContext.localContext("Accounts")]: [acc.account],
                        [CommonReportProps.dateRange]: CUSTOM_DATE_RANGE_ID,
                        [CommonReportProps.dateRangeCustomValue]: dateRange
                    },
                    context
                })
            }
        }
    ));
};

export async function getProfitLossData({ context }: IGetTileDataArgs) {
    const _checkZero = (amount: number, status: Status) => amount && status;

    const DESIRED_ACCOUNTS = ["428", "429"];
    const interval = await getOldestOpenFyInterval(context);
    const accounts = await getAnalysisReportData(DESIRED_ACCOUNTS, interval, Condition.Equals);

    const sums = accounts.reduce((acc, item) => {
            acc.credit += item.creditAmount || 0;
            acc.debit += item.debitAmount || 0;
            return acc;
        },
        { credit: 0, debit: 0 }
    );

    return [{
        value: sums.credit,
        severity: _checkZero(sums.credit, Status.Success),
        label: i18next.t("Home:ProfitLoss.Profit"),
        unit: "Kč"
    }, {
        severity: _checkZero(sums.debit, Status.Error),
        value: sums.debit,
        label: i18next.t("Home:ProfitLoss.Loss"),
        unit: "Kč"
    }];

}

const getAccountBalanceColumns = (): IColumn[] => {
    return [{
        id: "Account",
        label: i18next.t(`Home:LiabilitiesToShareholders.Account`),
        textAlign: TextAlign.Left
    }, {
        id: "Amount",
        label: i18next.t(`Home:LiabilitiesToShareholders.Amount`),
        textAlign: TextAlign.Right
    }];
};

export async function getCreditAccountBalancedTableData({ context }: IGetTileDataArgs) {
    const DESIRED_ACCOUNTS = ["231", "461", "379", "479"];

    const interval = await getOldestOpenFyInterval(context);
    const accounts = await getAnalysisReportData(DESIRED_ACCOUNTS, interval, Condition.Equals);

    return { columns: getAccountBalanceColumns(), rows: createAccountRows(accounts, context, interval) };
}

export async function getLiabilitiesToShareholdersTableData({ context }: IGetTileDataArgs) {
    const DESIRED_ACCOUNTS = ["36"];

    const interval = await getOldestOpenFyInterval(context);
    const accounts = (await getAnalysisReportData(DESIRED_ACCOUNTS, interval))?.filter(acc => acc.account?.length <=3);

    return { columns: getAccountBalanceColumns(), rows: createAccountRows(accounts, context, interval) };
}
